import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Button, ConfirmModal, Input, ModalShell } from '@/presentation/components/common';
import { useToast } from '@/presentation/contexts/ToastContext';
import { useAuth } from '@/presentation/contexts/AuthContext';
import { httpClient } from '@/infrastructure/http/client/httpClient';

type PanelGastoCategoria = {
  id: number;
  nombre: string;
  descripcion: string | null;
  activo: boolean;
};

export const ExpenseCategoriesPage: React.FC = () => {
  const { t } = useTranslation();
  const { canAdminister } = useAuth();
  const { success, error: showError } = useToast();
  const [isLoading, setIsLoading] = useState(true);
  const [categorias, setCategorias] = useState<PanelGastoCategoria[]>([]);
  const [editingCategoria, setEditingCategoria] = useState<PanelGastoCategoria | null>(null);
  const [categoriaNombre, setCategoriaNombre] = useState('');
  const [categoriaDescripcion, setCategoriaDescripcion] = useState('');
  const [isSavingCategoria, setIsSavingCategoria] = useState(false);
  const [showCategoryModal, setShowCategoryModal] = useState(false);
  const [confirmingCategoria, setConfirmingCategoria] = useState<PanelGastoCategoria | null>(null);
  const [isTogglingCategoria, setIsTogglingCategoria] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [sortField, setSortField] = useState<'nombre' | 'descripcion' | 'estado'>('nombre');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('asc');

  const load = async () => {
    setIsLoading(true);
    try {
      const res = await httpClient.get<PanelGastoCategoria[]>(
        '/balance/gasto-categorias?includeInactive=true'
      );
      setCategorias(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      showError(err instanceof Error ? err.message : t('common.error'));
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const resetCategoriaForm = () => {
    setEditingCategoria(null);
    setCategoriaNombre('');
    setCategoriaDescripcion('');
  };

  const openCreateModal = () => {
    resetCategoriaForm();
    setShowCategoryModal(true);
  };

  const handleEditCategoria = (categoria: PanelGastoCategoria) => {
    setEditingCategoria(categoria);
    setCategoriaNombre(categoria.nombre);
    setCategoriaDescripcion(categoria.descripcion || '');
    setShowCategoryModal(true);
  };

  const closeCategoryModal = () => {
    setShowCategoryModal(false);
    resetCategoriaForm();
  };

  const handleSaveCategoria = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!canAdminister) return;
    const nombre = categoriaNombre.trim();
    if (!nombre) {
      showError(t('pages.expenseCategories.nameRequired'));
      return;
    }

    setIsSavingCategoria(true);
    try {
      if (editingCategoria) {
        await httpClient.put<PanelGastoCategoria>(
          `/balance/gasto-categorias/${editingCategoria.id}`,
          {
            nombre,
            descripcion: categoriaDescripcion.trim() || null,
          }
        );
        success(t('pages.expenseCategories.updated'));
      } else {
        await httpClient.post<PanelGastoCategoria>('/balance/gasto-categorias', {
          nombre,
          descripcion: categoriaDescripcion.trim() || null,
          activo: true,
        });
        success(t('pages.expenseCategories.created'));
      }
      closeCategoryModal();
      await load();
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(axiosMsg || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setIsSavingCategoria(false);
    }
  };

  const handleToggleCategoria = async (categoria: PanelGastoCategoria) => {
    if (!canAdminister) return;
    setIsTogglingCategoria(true);
    try {
      const res = await httpClient.put<PanelGastoCategoria>(
        `/balance/gasto-categorias/${categoria.id}`,
        {
          activo: !categoria.activo,
        }
      );
      setCategorias((items) => items.map((item) => (item.id === categoria.id ? res.data : item)));
      success(
        res.data.activo
          ? t('pages.expenseCategories.activated')
          : t('pages.expenseCategories.deactivated')
      );
      if (editingCategoria?.id === categoria.id) {
        setEditingCategoria(res.data);
      }
      setConfirmingCategoria(null);
    } catch (err) {
      const axiosMsg =
        err && typeof err === 'object' && 'response' in err
          ? (err as { response?: { data?: { message?: string } } }).response?.data?.message
          : undefined;
      showError(axiosMsg || (err instanceof Error ? err.message : t('common.error')));
    } finally {
      setIsTogglingCategoria(false);
    }
  };

  const filteredCategorias = useMemo(() => {
    const normalizedSearch = searchTerm.trim().toLowerCase();
    const filtered = categorias.filter((categoria) => {
      const matchesSearch =
        !normalizedSearch ||
        categoria.nombre.toLowerCase().includes(normalizedSearch) ||
        (categoria.descripcion || '').toLowerCase().includes(normalizedSearch);
      const matchesStatus =
        statusFilter === 'all' ||
        (statusFilter === 'active' && categoria.activo) ||
        (statusFilter === 'inactive' && !categoria.activo);
      return matchesSearch && matchesStatus;
    });

    return filtered.sort((a, b) => {
      const direction = sortDirection === 'asc' ? 1 : -1;
      let compare = 0;
      if (sortField === 'nombre') {
        compare = a.nombre.localeCompare(b.nombre, 'es');
      } else if (sortField === 'descripcion') {
        compare = (a.descripcion || '').localeCompare(b.descripcion || '', 'es');
      } else {
        compare = Number(a.activo) - Number(b.activo);
      }
      return compare * direction;
    });
  }, [categorias, searchTerm, sortDirection, sortField, statusFilter]);

  const handleSort = (field: typeof sortField) => {
    if (sortField === field) {
      setSortDirection((direction) => (direction === 'asc' ? 'desc' : 'asc'));
      return;
    }
    setSortField(field);
    setSortDirection('asc');
  };

  const sortIcon = (field: typeof sortField) => {
    if (sortField !== field) return ' ↕';
    return sortDirection === 'asc' ? ' ↑' : ' ↓';
  };

  const clearFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
  };

  const activeFilterChips = [
    ...(searchTerm.trim()
      ? [
          {
            key: 'search',
            label: `${t('common.search')}: ${searchTerm.trim()}`,
            onRemove: () => setSearchTerm(''),
          },
        ]
      : []),
    ...(statusFilter !== 'all'
      ? [
          {
            key: 'status',
            label: `${t('pages.expenseCategories.status')}: ${
              statusFilter === 'active'
                ? t('pages.expenseCategories.active')
                : t('pages.expenseCategories.inactive')
            }`,
            onRemove: () => setStatusFilter('all'),
          },
        ]
      : []),
  ];

  const confirmCategoriaIsActive = confirmingCategoria?.activo ?? false;

  return (
    <div className="panel-page h-full min-w-0 flex flex-col overflow-auto">
      <div className="panel-page-header">
        <div className="min-w-0">
          <h1 className="panel-page-title">{t('pages.expenseCategories.title')}</h1>
          <p className="panel-page-subtitle">{t('pages.expenseCategories.subtitle')}</p>
        </div>
        {canAdminister ? (
          <Button type="button" size="sm" onClick={openCreateModal}>
            {t('common.create')}
          </Button>
        ) : null}
      </div>

      <section className="bg-white dark:bg-gray-800 rounded-xl border border-slate-200 dark:border-gray-700 shadow-sm p-3 space-y-3">
        <div className="rounded-lg border border-slate-200 bg-white p-2 shadow-sm dark:border-gray-700 dark:bg-gray-800">
          <div className="flex flex-col gap-2 lg:flex-row lg:items-center">
            <div className="grid flex-1 grid-cols-1 gap-2 md:grid-cols-[minmax(18rem,1fr)_minmax(10rem,0.3fr)]">
              <Input
                type="text"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder={t('pages.expenseCategories.searchPlaceholder')}
                className="h-8 py-1 text-xs"
                aria-label={t('common.search')}
                title={t('common.search')}
              />
              <select
                value={statusFilter}
                onChange={(e) =>
                  setStatusFilter(e.target.value as 'all' | 'active' | 'inactive')
                }
                className="block h-8 w-full rounded-md border border-gray-300 bg-white px-2 text-xs text-gray-900 focus:border-teal-500 focus:outline-none focus:ring-2 focus:ring-teal-500 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
                aria-label={t('pages.expenseCategories.status')}
                title={t('pages.expenseCategories.status')}
              >
                <option value="all">{t('pages.expenseCategories.allStatuses')}</option>
                <option value="active">{t('pages.expenseCategories.active')}</option>
                <option value="inactive">{t('pages.expenseCategories.inactive')}</option>
              </select>
            </div>

            <button
              type="button"
              onClick={clearFilters}
              disabled={activeFilterChips.length === 0}
              className="inline-flex h-8 w-full items-center justify-center rounded-md border border-slate-300 bg-white text-slate-500 transition-colors hover:bg-slate-50 hover:text-slate-800 disabled:cursor-not-allowed disabled:opacity-40 dark:border-gray-600 dark:bg-gray-700 dark:text-slate-300 dark:hover:bg-gray-600 dark:hover:text-white lg:w-8"
              title={t('pages.expenseCategories.clearFilters')}
              aria-label={t('pages.expenseCategories.clearFilters')}
            >
              <svg className="h-3.5 w-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.75} aria-hidden>
                <path strokeLinecap="round" strokeLinejoin="round" d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6M9 7V4a1 1 0 011-1h4a1 1 0 011 1v3m-8 0h10" />
              </svg>
              <span className="ml-2 text-sm lg:hidden">
                {t('pages.expenseCategories.clearFilters')}
              </span>
            </button>
          </div>

          {activeFilterChips.length > 0 ? (
            <div className="mt-2 flex flex-wrap gap-1.5">
              {activeFilterChips.map((chip) => (
                <button
                  key={chip.key}
                  type="button"
                  onClick={chip.onRemove}
                  className="inline-flex max-w-full items-center gap-1.5 rounded-full border border-teal-500/50 bg-teal-50 px-2 py-0.5 text-[11px] font-medium leading-4 text-teal-700 transition-colors hover:bg-teal-100 dark:border-teal-400/50 dark:bg-teal-900/20 dark:text-teal-200 dark:hover:bg-teal-900/40"
                  title={chip.label}
                >
                  <span className="truncate">{chip.label}</span>
                  <span className="text-sm leading-none" aria-hidden>×</span>
                </button>
              ))}
            </div>
          ) : null}
        </div>

        <div className="overflow-auto rounded-lg border border-slate-200 dark:border-gray-700">
          <table className="min-w-full text-sm">
            <thead className="bg-slate-50 text-left text-xs uppercase tracking-wide text-slate-500 dark:bg-gray-900 dark:text-slate-400">
              <tr>
                <th
                  className="cursor-pointer select-none px-3 py-2 hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('nombre')}
                >
                  {t('pages.expenseCategories.name')}
                  {sortIcon('nombre')}
                </th>
                <th
                  className="cursor-pointer select-none px-3 py-2 hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('descripcion')}
                >
                  {t('pages.expenseCategories.description')}
                  {sortIcon('descripcion')}
                </th>
                <th
                  className="cursor-pointer select-none px-3 py-2 hover:bg-slate-100 dark:hover:bg-gray-800"
                  onClick={() => handleSort('estado')}
                >
                  {t('pages.expenseCategories.status')}
                  {sortIcon('estado')}
                </th>
                <th className="px-3 py-2 text-right">{t('common.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {isLoading ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                    {t('common.loading')}
                  </td>
                </tr>
              ) : filteredCategorias.length === 0 ? (
                <tr>
                  <td colSpan={4} className="px-3 py-6 text-center text-slate-500">
                    {categorias.length === 0
                      ? t('pages.expenseCategories.empty')
                      : t('common.noResults')}
                  </td>
                </tr>
              ) : (
                filteredCategorias.map((categoria) => (
                  <tr key={categoria.id} className="border-t border-slate-100 dark:border-gray-700">
                    <td className="px-3 py-2 font-medium text-slate-900 dark:text-white">
                      {categoria.nombre}
                    </td>
                    <td className="px-3 py-2 text-slate-500 dark:text-slate-400">
                        {categoria.descripcion || '-'}
                    </td>
                    <td className="px-3 py-2">
                      <span
                        className={`rounded-full px-2 py-0.5 text-xs font-semibold ${
                          categoria.activo
                            ? 'bg-green-100 text-green-700 dark:bg-green-900/40 dark:text-green-200'
                            : 'bg-slate-100 text-slate-500 dark:bg-gray-700 dark:text-slate-300'
                        }`}
                      >
                        {categoria.activo
                          ? t('pages.expenseCategories.active')
                          : t('pages.expenseCategories.inactive')}
                      </span>
                    </td>
                    <td className="px-3 py-2">
                      <div className="flex justify-end gap-2">
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={!canAdminister}
                          onClick={() => handleEditCategoria(categoria)}
                        >
                          {t('common.edit')}
                        </Button>
                        <Button
                          type="button"
                          variant="secondary"
                          size="sm"
                          disabled={!canAdminister}
                          onClick={() => setConfirmingCategoria(categoria)}
                        >
                            {categoria.activo
                              ? t('pages.expenseCategories.deactivate')
                              : t('pages.expenseCategories.activate')}
                        </Button>
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </section>

      <ModalShell
        isOpen={showCategoryModal}
        onClose={closeCategoryModal}
        title={
          editingCategoria
            ? t('pages.expenseCategories.editTitle')
            : t('pages.expenseCategories.createTitle')
        }
        subtitle={t('pages.expenseCategories.modalSubtitle')}
        size="md"
        footer={
          <>
            <Button
              type="button"
              variant="secondary"
              onClick={closeCategoryModal}
              disabled={isSavingCategoria}
            >
              {t('common.cancel')}
            </Button>
            <Button
              type="submit"
              form="expense-category-form"
              disabled={!canAdminister || isSavingCategoria}
              isLoading={isSavingCategoria}
            >
              {t('common.save')}
            </Button>
          </>
        }
      >
        <form id="expense-category-form" onSubmit={handleSaveCategoria} className="space-y-4">
          <Input
            label={t('pages.expenseCategories.name')}
            type="text"
            value={categoriaNombre}
            onChange={(e) => setCategoriaNombre(e.target.value)}
            placeholder={t('pages.expenseCategories.namePlaceholder')}
            disabled={!canAdminister || isSavingCategoria}
            autoFocus
          />
          <Input
            label={t('pages.expenseCategories.description')}
            type="text"
            value={categoriaDescripcion}
            onChange={(e) => setCategoriaDescripcion(e.target.value)}
            placeholder={t('pages.expenseCategories.descriptionPlaceholder')}
            disabled={!canAdminister || isSavingCategoria}
          />
        </form>
      </ModalShell>

      <ConfirmModal
        isOpen={Boolean(confirmingCategoria)}
        onClose={() => setConfirmingCategoria(null)}
        onConfirm={() => {
          if (!confirmingCategoria) return;
          return handleToggleCategoria(confirmingCategoria);
        }}
        title={
          confirmCategoriaIsActive
            ? t('pages.expenseCategories.confirmDeactivateTitle')
            : t('pages.expenseCategories.confirmActivateTitle')
        }
        message={
          confirmCategoriaIsActive
            ? t('pages.expenseCategories.confirmDeactivateMessage', {
                name: confirmingCategoria?.nombre,
              })
            : t('pages.expenseCategories.confirmActivateMessage', {
                name: confirmingCategoria?.nombre,
              })
        }
        confirmText={
          confirmCategoriaIsActive
            ? t('pages.expenseCategories.deactivate')
            : t('pages.expenseCategories.activate')
        }
        variant={confirmCategoriaIsActive ? 'danger' : 'primary'}
        isLoading={isTogglingCategoria}
      />
    </div>
  );
};
